import Vue from "vue";
import axios from "axios";
import apiGraph, { graphGet } from "@/helpers/api/apiGraph";
import apiSkobo from "@/helpers/api/apiSkobo";
import vuexStore from "@/helpers/store";
import DriveItemHelper from "@/models/helpers/DriveItemHelper";
import FileHelper from "@/helpers/utilities/FileHelper";
import { useFeaturesStore } from "@/stores/features";
import { useMeStore } from "@/stores/me";
import { useGlobalsStore } from "@/stores/globals";
import { useLogsStore } from "@/stores/logs";

const uploadFileSizeSplitLimit = 5242880;

export const documentFunctions_state = {
  journalizedAndLockedDocuments: {},

  downloadProgressState: {},
  confidentialDocumentsSharedToAulaForUserDriveMap: {},

  confidentialDocumentsJournalizedPerUserMap: {},
  confidentialDocumentsJournalizedPerSectionMap: {},
  hiddenSharedFilesState: []
};

export const documentFunctions_actions = {
  /**
   * Create new document in destination from
   * @param { Array<object> } templateItems Array of templates to create driveItem from
   * @param { string } destinationDriveId Destination drive id to copy to
   * @param { string } destinationItemId Destination item id to copy to
   * @returns {Promise<object>} Response with new item
   */
  async createNewDriveItemFromTemplate(templateItems, destinationDriveId, destinationItemId) {
    const response = await this.doCopyDriveItems(templateItems, destinationDriveId, destinationItemId);

    this.logActivity(destinationDriveId, response[0].itemId, destinationItemId, "CreateDriveItem", {
      templateId: templateItems[0].parentReference.driveId,
      templateDriveId: templateItems[0].id,
      name: templateItems[0].name
    });
    return response;
  },

  /**
   * Copies driveItems to destination drive
   * @param { Array<object> } itemsToCopy Array of items to copy
   * @param { string } destinationDriveId Destination drive id to copy to
   * @param { string } destinationItemId Destination item id to copy to
   * @param { boolean } cloneVersionInsteadOfCopy  True if version should be cloned instead of copied
   * @param { boolean } replaceItemOnConflict  True if item should be replaced on conflict
   * @returns { Array<object> } Array of copied items { driveId, itemId }
   */
  async copyDriveItems(
    itemsToCopy,
    destinationDriveId,
    destinationItemId,
    cloneVersionInsteadOfCopy = false,
    replaceItemOnConflict = false
  ) {
    const response = await this.doCopyDriveItems(
      itemsToCopy,
      destinationDriveId,
      destinationItemId,
      cloneVersionInsteadOfCopy,
      replaceItemOnConflict
    );

    for (let i = 0; i < itemsToCopy.length; i++) {
      this.logActivity(destinationDriveId, response[i].itemId, destinationItemId, "CopyDriveItem", {
        templateId: itemsToCopy[i].parentReference.driveId,
        templateDriveId: itemsToCopy[i].id,
        name: itemsToCopy[i].name
      });
    }

    return response;
  },

  /**
   * Copies driveItems to destination drive
   * @param { Array<object> } itemsToCopy Array of items to copy
   * @param {*} destinationDriveId Destination drive id to copy to
   * @param {*} destinationItemId Destination item id to copy to
   * @param {*} cloneVersionInsteadOfCopy  True if version should be cloned instead of copied
   * @param {*} replaceItemOnConflict  True if item should be replaced on conflict
   * @returns { Array<object> } Array of copied items { driveId, itemId }
   */
  async doCopyDriveItems(
    itemsToCopy,
    destinationDriveId,
    destinationItemId,
    cloneVersionInsteadOfCopy = false,
    replaceItemOnConflict = false
  ) {
    const promises = await itemsToCopy.map(async itm => {
      try {
        const response = await apiGraph.post(`/drives/${itm.parentReference.driveId}/items/${itm.id}/copy`, {
          parentReference: {
            driveId: destinationDriveId,
            id: DriveItemHelper.getItemId(destinationItemId)
          },
          name: itm.newName || itm.name
        });

        if (response.data.driveId && response.data.id) {
          // If the item was copied successfully (OnPremises)
          //Do not reload destination drive here though it seems tempting. Other functions use copy where reloading is not wanted.
          return {
            driveId: response.data.driveId,
            itemId: response.data.id
          };
        } else {
          // Waiting for copy process to complete (MS365)

          while (true) {
            const completeResponse = await axios.get(response.headers.location);
            if (
              completeResponse &&
              completeResponse.data.percentageComplete === 100 &&
              completeResponse.data.status === "completed"
            ) {
              await this.reloadDriveItem(destinationDriveId, destinationItemId);

              return {
                driveId: destinationDriveId,
                itemId: completeResponse.data.resourceId
              };
            } else if (
              completeResponse.data.error ||
              (completeResponse.data.percentageComplete !== 100 && completeResponse.data.status === "completed")
            ) {
              if (completeResponse.data.error.code === "nameAlreadyExists") {
                if (replaceItemOnConflict) {
                  await this.loadDriveItem(destinationDriveId, destinationItemId);
                  const destinationChildren = this.driveItemChildren(destinationDriveId, destinationItemId);
                  const conflictingItem = destinationChildren.find(child => child.name === itm.name);

                  // Delete conflicting items
                  if (conflictingItem) {
                    try {
                      await this.deleteDriveItems([conflictingItem]);
                      // eslint-disable-next-line no-empty
                    } catch {}

                    return await this.copyDriveItems(
                      [itm],
                      destinationDriveId,
                      destinationItemId,
                      cloneVersionInsteadOfCopy,
                      replaceItemOnConflict
                    );
                  }
                } else {
                  // Rename and try copying again
                  const newItm = JSON.parse(JSON.stringify(itm));
                  if (cloneVersionInsteadOfCopy) {
                    newItm.name = DriveItemHelper.calcNextVersionName(newItm.name);
                  } else {
                    newItm.name = DriveItemHelper.calcNewCopyName(newItm.name);
                  }
                  return await this.copyDriveItems(
                    [newItm],
                    destinationDriveId,
                    destinationItemId,
                    cloneVersionInsteadOfCopy,
                    replaceItemOnConflict
                  );
                }
              }
            }
          }
        }
      } catch (err) {
        if (
          err.response?.data &&
          (err.response.data.error?.code === "nameAlreadyExists" || err.response.data == "Name already exists")
        ) {
          // Rename and try copying again
          const newItm = JSON.parse(JSON.stringify(itm));
          if (cloneVersionInsteadOfCopy) {
            newItm.name = DriveItemHelper.calcNextVersionName(newItm.name);
          } else {
            newItm.name = DriveItemHelper.calcNewCopyName(newItm.name);
          }
          const res = await this.copyDriveItems(
            [newItm],
            destinationDriveId,
            destinationItemId,
            cloneVersionInsteadOfCopy,
            replaceItemOnConflict
          );
          return res;
        } else {
          throw Error(err);
        }
      }
    });

    const results = (await Promise.all(promises)).flat();
    //Do not reload destination drive here though it seems tempting. Other functions use copy where reloading is not wanted.
    return results;
  },

  /**
   * Move array of driveItems to new location
   * @param { Array<object>} itemsToMove DriveItems to move
   * @param { string } destinationDriveId Destinations driveId
   * @param { string } destinationItemId Destination itemId
   */
  async moveDriveItems(itemsToMove, destinationDriveId, destinationItemId) {
    let calcDestinationDriveId = this.calculateDriveIdOrMyDrive(destinationDriveId);

    // Check if destination is a section subdrive
    // TODO: Waiting for refactoring of sectionRootDriveSettingMap
    /*
    if (!destinationItemId) {
      const parentDriveId = rootGetters["sections/PARENT_OF_SUB_DRIVE"](calcDestinationDriveId);
      if (parentDriveId) {
        calcDestinationDriveId = parentDriveId;
      }
    }
      */

    const calcDestinationItemId = this.calculateRootOrItemId(calcDestinationDriveId, destinationItemId);

    if (itemsToMove.length > 0) {
      const sourceDriveIdToUpdate = itemsToMove[0].parentReference.driveId;
      const sourceItemIdToUpdate = itemsToMove[0].parentReference.id;

      const progressPromises = itemsToMove.map(itm => {
        // Check if destination and source is the same
        if (itm.parentReference.driveId !== calcDestinationDriveId || itm.id !== calcDestinationItemId) {
          return apiGraph.patch(`/drives/${itm.parentReference.driveId}/items/${itm.id}`, {
            parentReference: {
              driveId: calcDestinationDriveId,
              id: calcDestinationItemId
            },
            name: itm.newName || itm.name
          });
        }
      });

      await Promise.all(progressPromises);

      // Update source
      this.reloadDriveItem(sourceDriveIdToUpdate, sourceItemIdToUpdate);

      // Update destination
      this.reloadDriveItem(calcDestinationDriveId, calcDestinationItemId);

      itemsToMove.forEach(item => {
        this.logActivity(calcDestinationDriveId, item.Id, calcDestinationItemId, "MoveDriveItem", {
          itemId: item.id,
          destinationItemId: calcDestinationItemId,
          name: item.name,
          newName: item.newName
        });
      });
    }
  },

  /**
   * Rename driveItem
   * @param { string } driveId id of drive to load
   * @param { string } itemId id of driveItem to load
   * @param { string } parentItemId parent items id
   * @param { string } newDocumentName New name for drive item
   */
  async renameDriveItem(driveId, itemId, parentItemId, newDocumentName) {
    let headers = {
      Prefer: "bypass-shared-lock"
    };

    if (import.meta.env.VITE_BUILD_TARGET === "kkfiler") {
      headers = {};
    }

    const oldChildren = this.driveItemChildren(driveId, parentItemId);

    await apiGraph.patch(`/drives/${driveId}/items/${itemId}`, { name: newDocumentName }, { headers });
    apiSkobo.patch(`/AppDriveItems/${driveId}/${itemId}`, { newDisplayName: newDocumentName }, { headers });
    await this.reloadDriveItem(driveId, parentItemId);
    const oldName = oldChildren.find(child => child.id === itemId).name || "";

    this.logActivity(driveId, itemId, parentItemId, "Rename", { oldName, newName: newDocumentName });
  },

  /**
   * Download driveItem
   * @param { string } driveId id of drive to download
   * @param { string } itemId id of driveItem to download
   * @param { string } requestId Request id
   * @returns { object } File response from api
   */
  async downloadSingleFile(driveId, itemId, requestId) {
    const urlResponse = await graphGet(`/drives/${driveId}/items/${itemId}?select=id,@microsoft.graph.downloadUrl`);

    const configForApiRequest = {
      headers: {
        Accept: "application/octet-stream"
      },
      responseType: "blob",
      onDownloadProgress: progressEvent => {
        Vue.set(this.downloadProgressState, requestId, Math.floor((progressEvent.loaded / progressEvent.total) * 100));
      }
    };

    Vue.set(this.downloadProgressState, requestId, -1);

    const fileResponse = await graphGet(urlResponse["@microsoft.graph.downloadUrl"], configForApiRequest);
    this.logActivity(driveId, itemId, null, "Download driveItem");
    return fileResponse;
  },

  /**
   * Download driveItems and folders
   * @param { object[] } driveItems Drive items to delete
   * @param { string } requestId Request id
   * @returns { Array<any> } Array of file bytes downloaded from api
   */
  async downloadMultipleDriveItemsAndFolders(driveItems, requestId) {
    try {
      const parentDriveId = driveItems[0].parentReference.driveId;
      const driveItemIds = driveItems.map(d => d.id);
      const itemIdArray = driveItemIds;
      let joinedDriveItemIds = itemIdArray.join();

      const configForApiRequest = {
        headers: {
          Accept: "application/octet-stream"
        },
        responseType: "blob",
        onDownloadProgress: progressEvent => {
          Vue.set(
            this.downloadProgressState,
            requestId,
            Math.floor((progressEvent.loaded / progressEvent.total) * 100)
          );
        }
      };

      Vue.set(this.downloadProgressState, requestId, -1);

      const response = await apiSkobo.get(
        `/appdriveitems/zip/drives/${parentDriveId}/?items=${joinedDriveItemIds}`,
        configForApiRequest
      );

      this.logActivity(parentDriveId, driveItemIds[0], null, "Download multiple driveItems", { itemIds: driveItemIds });
      return response;
    } catch {
      this.$skboMessage.errorMessage(this.$t("Documents.Errors.ErrorDuringDownload"));
    }
  },

  /**
   * Delete driveItem
   * @param { object[] } driveItems Drive items to delete
   */
  async deleteDriveItems(driveItems) {
    const parentDriveId = driveItems[0].parentReference.driveId;
    const parentItemId = driveItems[0].parentReference.id;

    const promises = [];

    driveItems.forEach(item => {
      promises.push(apiGraph.delete(`/drives/${item.parentReference.driveId}/items/${item.id}`));
      promises.push(apiSkobo.delete(`/AppDriveItems/${item.parentReference.driveId}/${item.id}`));
    });

    await Promise.all(promises);

    const reloadPromises = [];
    promises.push(this.reloadDriveItem(parentDriveId, parentItemId));

    await Promise.all(reloadPromises);
    // Log
    driveItems.forEach(item => {
      this.logActivity(item.parentReference.driveId, item.id, item.parentReference.id, "Delete", { name: item.name });
    });
  },

  /**
   * Upload file to drive
   * @param {*} destinationDriveId DriveId of destination folder
   * @param {*} destinationItemId ItemId of destination folder
   * @param {*} fileObject File object to upload
   * @param {*} replaceItemOnConflict If true file will be replaced on conflict - else renamed
   * @returns { Promise } Upload response
   */
  async uploadDriveItem(destinationDriveId, destinationItemId, fileObject, replaceItemOnConflict) {
    const session = await createUploadSession(
      destinationDriveId,
      destinationItemId,
      fileObject.name,
      replaceItemOnConflict
    );

    const binary = await FileHelper.getFileAsBinary(fileObject.file);
    const fileChunks = splitFileToChuncks(binary, uploadFileSizeSplitLimit);
    let chunkUploadResponse;
    let contentRangePositionCounter = 0;

    if (import.meta.env.VITE_BUILD_TARGET === "kkfiler") {
      for (let i = 0; i < fileChunks.length; i++) {
        const contentRange = `bytes ${contentRangePositionCounter}-${
          contentRangePositionCounter + (fileChunks[i].byteLength - 1)
        }/${binary.byteLength}`;

        await apiGraph.put(session.data.uploadUrl, fileChunks[i], {
          headers: {
            "Content-Length": fileChunks[i].byteLength,
            "Content-Range": contentRange,
            "Content-Type": "application/octet-stream"
          }
        });

        contentRangePositionCounter += fileChunks[i].byteLength;
      }

      this.reloadDriveItem(destinationDriveId, destinationItemId);
      this.logActivity(destinationDriveId, destinationItemId, null, "Upload", { name: fileObject.name });
    } else {
      for (let i = 0; i < fileChunks.length; i++) {
        const contentRange = `bytes ${contentRangePositionCounter}-${
          contentRangePositionCounter + (fileChunks[i].byteLength - 1)
        }/${binary.byteLength}`;

        chunkUploadResponse = await apiGraph.put(session.data.uploadUrl, fileChunks[i], {
          headers: {
            "Content-Length": fileChunks[i].byteLength,
            "Content-Range": contentRange,
            "Content-Type": "application/octet-stream"
          }
        });

        contentRangePositionCounter += fileChunks[i].byteLength;
      }

      this.reloadDriveItem(destinationDriveId, destinationItemId);

      this.logActivity(destinationDriveId, destinationItemId, null, "Upload", {
        name: fileObject.name,
        size: fileObject.size / 1048576
      });
      return chunkUploadResponse.data;
    }
  },

  /**
   * Create a folder on drive and item
   * @param  { string } driveId DriveId to create folder in
   * @param { string } parentItemId ItemId to created folder in
   * @param { string } name Name of new folder
   * @returns { Promise<object> } Promise with http response
   */
  async createFolder(driveId, parentItemId, name) {
    const response = await apiGraph.post(`${DriveItemHelper.getDriveUri(driveId)}/items/${parentItemId}/children`, {
      name: name,
      folder: {},
      "@microsoft.graph.conflictBehavior": "rename"
    });

    this.reloadDriveItem(driveId, parentItemId);

    return response;
  },

  // Journalization

  async loadJournalizingAndLockingDocumentsForPersonAndDrive(personId, driveId) {
    if (
      !this.journalizingAndLockingDocumentsPerPersonAndDriveMap[personId] ||
      !this.journalizingAndLockingDocumentsPerPersonAndDriveMap[personId][driveId]
    ) {
      return await this.reloadJournalizingAndLockingDocumentsForPersonAndDrive(personId, driveId);
    }
  },

  async reloadJournalizingAndLockingDocumentsForPersonAndDrive(personId, driveId) {
    const response = await apiSkobo.get(`/appconfidentialfolder/journalizedFiles/user/${personId}`);
    const stateClone = Object.assign({}, this.journalizingAndLockingDocumentsPerPersonAndDriveMap);

    if (!stateClone[personId]) {
      stateClone[personId] = {};
    }

    stateClone[personId][driveId] = response.data.filter(doc => doc.status !== "finished");
    Vue.set(this, "journalizingAndLockingDocumentsPerPersonAndDriveMap", stateClone);
  },

  async loadJournalizingAndLockingDocumentsForSectionAndDrive(sectionId, driveId) {
    if (
      !this.journalizingAndLockingDocumentsPerSectionAndDriveMap[sectionId] ||
      !this.journalizingAndLockingDocumentsPerSectionAndDriveMap[sectionId][driveId]
    ) {
      return await this.reloadJournalizingAndLockingDocumentsForSectionAndDrive(sectionId, driveId);
    }
  },

  async reloadJournalizingAndLockingDocumentsForSectionAndDrive(sectionId, driveId) {
    const response = await apiSkobo.get(`/appconfidentialfolder/journalizedFiles/user/${sectionId}`);
    const stateClone = Object.assign({}, this.journalizingAndLockingDocumentsPerSectionAndDriveMap);

    if (!stateClone[sectionId]) {
      stateClone[sectionId] = {};
    }

    stateClone[sectionId][driveId] = response.data.filter(doc => doc.status !== "finished");
    Vue.set(this, "journalizingAndLockingDocumentsPerPersonAndDriveMap", stateClone);
  },

  ////////////////////////
  // Share to AULA
  ///////////////////////

  /**
   * Share document with relation through Aula
   * @param { string } userId Userid of relation to share driveItem with
   * @param { string } driveId DriveId of item to share
   * @param { string } itemId ItemId of item to share
   * @param { string } expireDateTime Date driveItem should no longer be shared
   * @param { string } fileName Name of driveItem shared
   */
  async addDocumentSharedToRelation(userId, driveId, itemId, expireDateTime, fileName) {
    await apiSkobo.post(`/appconfidentialfolder/sharetorelations`, {
      userId,
      driveId,
      itemId,
      expireDateTime,
      fileName
    });

    await this.reloadConfidentialDocumentsSharedToAulaForUserOnDrive(userId, driveId);
    this.logActivity(driveId, itemId, null, "AddShareToAula", { fileName, userId, expireDateTime });
  },

  /**
   * Remove share of driveItem through Aula
   * @param { string } userId User id of relation share should be removed from
   * @param { string } driveId DriveId of the shared item
   * @param { string } itemId ItemId of the shared item
   */
  async deleteDocumentSharedToRelation(userId, driveId, itemId) {
    await apiSkobo.delete(`/appconfidentialfolder/sharetorelations/${userId}/${driveId}/${itemId}`);
    await this.reloadConfidentialDocumentsSharedToAulaForUserOnDrive(userId, driveId);
    this.logActivity(driveId, itemId, null, "DeleteShareToAula", { userId });
  },

  /**
   * Load users shared confidential documents for drive
   * @param { string } userId Users id
   * @param { string } driveId Drives id
   * @returns { Promise } void
   */
  async loadConfidentialDocumentsSharedToAulaForUserOnDrive(userId, driveId) {
    if (
      !this.confidentialDocumentsSharedToAulaForUserDriveMap[userId] ||
      !this.confidentialDocumentsSharedToAulaForUserDriveMap[userId][driveId]
    ) {
      await this.reloadConfidentialDocumentsSharedToAulaForUserOnDrive(userId, driveId);
    }
  },

  /**
   * Reload users shared confidential documents for drive
   * @param { string } userId Users id
   * @param { string } driveId Drives id
   * @returns { Promise } void
   */
  async reloadConfidentialDocumentsSharedToAulaForUserOnDrive(userId, driveId) {
    const response = await apiSkobo.get(`/appconfidentialfolder/sharetorelations/${userId}/${driveId}`);

    const stateClone = Object.assign({}, this.confidentialDocumentsSharedToAulaForUserDriveMap);

    if (!stateClone[userId]) {
      stateClone[userId] = {};
    }

    stateClone[userId][driveId] = response.data;
    this.confidentialDocumentsSharedToAulaForUserDriveMap = stateClone;
  },

  /**
   *
   * @param { string } appConfidentialId AppConfidentialId of the container of the document
   * @param { Array<object>} driveItems DriveItems that should be locked
   * @returns { Promise } Promise of completion
   */
  async lockDocuments(appConfidentialId, driveItems) {
    const featuresStore = useFeaturesStore();
    if (!featuresStore.isFeatureEnabled("DocumentConfidentialLock")) {
      return;
    }

    try {
      const initLockPromises = driveItems.map(item =>
        apiSkobo.post(`/appconfidentialfolder/${appConfidentialId}/lock`, {
          driveId: item.parentReference.driveId,
          itemId: item.id,
          fileName: item.name
        })
      );

      const initLockResults = await Promise.allSettled(initLockPromises);

      const awaitLockPromises = initLockResults.map(async promiseResult => {
        if (promiseResult.status === "rejected") {
          const globalsStore = useGlobalsStore();
          if (promiseResult?.reason?.response?.data.includes("is locked for shared use")) {
            globalsStore.setErrorMessage("$t('Documents.Errors.LockDocumentToDocumentIsLockedBySomeoneElse')");
          } else if (promiseResult?.reason?.response?.data?.error?.startsWith("This file has already been locked")) {
            globalsStore.setErrorMessage("$t('Errors.Lock.AlreadyLocked')");
          } else {
            globalsStore.setErrorMessage("$t('Errors.Lock.AnErrorOccurred')");
          }
        } else {
          return;
        }
      });

      await Promise.all(awaitLockPromises);
      await this.loadJournalizedAndLockedDocuments(appConfidentialId);

      const parent = driveItems[0].parentReference;

      this.reload(parent.driveId, parent.id);

      driveItems.map(d => this.reloadDocumentPermissions(d.parentReference.driveId, d.id));

      driveItems.forEach(item => {
        this.reloadDocumentPermissions(item.parentReference.driveId, item.id);

        this.logActivity(item.parentReference.driveId, item.id, null, "Lock document");
      });
    } catch {
      const globalsStore = useGlobalsStore();
      globalsStore.setErrorMessage("$t('Errors.Lock.AnErrorOccurred')");
      throw new Error();
    }
  },

  async loadJournalizedAndLockedDocuments(appConfidentialId) {
    if (!this.journalizedAndLockedDocuments[appConfidentialId]) {
      await this.reloadJournalizedAndLockedDocuments(appConfidentialId);
    }
    return this.journalizedAndLockedDocuments[appConfidentialId];
  },

  async reloadJournalizedAndLockedDocuments(appConfidentialId) {
    const response = await apiSkobo.get(`/appconfidentialfolder/${appConfidentialId}/itemstatus`);

    const stateClone = Object.assign({}, this.journalizedAndLockedDocuments);
    stateClone[appConfidentialId] = response.data;
    Vue.set(this, "JournlizedAndLockedDocuments", stateClone);
  },

  async hideDriveItems(driveItems) {
    await driveItems.map(async dr => {
      const driveId = DriveItemHelper.getDriveId(dr);
      const itemId = dr.id;

      const meStore = useMeStore();
      const userId = meStore.userId;
      let existingHidden = new Array(...this.hiddenSharedFilesState);
      if (!existingHidden) {
        existingHidden = [];
      }
      existingHidden.push({ driveId, itemId });

      await axios.post(
        `https://skobostats.azurewebsites.net/api/UserSettings?code=TCsYhdXzGIodM/bwslBH/eCvoW4vO0He3HHzwBkmlrSk1r29clZJzQ==`,
        { key: "hiddenSharedFilesState", value: JSON.stringify(existingHidden), userId }
      );

      const listInState = this.hiddenSharedFilesState;
      listInState.push({ driveId, itemId });
      Vue.set(this, "hiddenSharedFilesState", listInState);
    });

    const parentDriveId = DriveItemHelper.getDriveId(driveItems[0]);
    const parentItemId = driveItems[0].remoteItem
      ? driveItems[0].remoteItem.parentReference.id
      : driveItems[0].parentReference.id;

    this.reloadDriveItem(parentDriveId, parentItemId);
    vuexStore.dispatch("loadDriveItemsSharedWithMe");
  },

  logActivity(driveId, itemId, parentItemId, action, metadata) {
    let keys;

    try {
      keys = this.controlledAreaKeysFromDriveIdAndItemId(driveId, itemId);
    } catch {
      let parentItem;
      try {
        if (parentItemId) {
          keys = this.controlledAreaKeysFromDriveIdAndItemId(driveId, parentItemId);
        } else {
          parentItem = this.parentDriveItem(driveId, itemId);
        }

        if (parentItem) {
          keys = this.controlledAreaKeysFromDriveIdAndItemId(driveId, parentItem.id);
        }
        // eslint-disable-next-line no-empty
      } catch {}
    }

    if (keys) {
      try {
        // Log
        const confidentialId = this.controlledAreaConfidentialId(keys.system, keys.entity, keys.group);

        if (confidentialId) {
          const logsStore = useLogsStore();
          logsStore.logActivity(confidentialId, keys?.system, keys?.entity, driveId, itemId, action, metadata);
        }
        // eslint-disable-next-line no-empty
      } catch {} // No need to catch it at the moment - error will happen
    }
  }
};

export const documentFunctions_getters = {
  isDocumentJournalizing: state => (appConfidentialId, driveId, itemId) => {
    if (state.journalizedAndLockedDocuments[appConfidentialId]) {
      return state.journalizedAndLockedDocuments[appConfidentialId].some(
        doc => doc.itemId === itemId && doc.driveId === driveId && doc.type === "Journalize"
      );
    }
  },

  isDocumentLocking: state => (appConfidentialId, driveId, itemId) => {
    if (state.journalizedAndLockedDocuments[appConfidentialId]) {
      return state.journalizedAndLockedDocuments[appConfidentialId].some(
        doc => doc.itemId === itemId && doc.driveId === driveId && doc.type === "Lock"
      );
    }
  },

  downloadProgress: state => requestId => {
    return state.downloadProgressState[requestId];
  },

  confidentialDocumentsSharedToAulaForUserIdOnDriveId: state => (userId, driveId) => {
    return state.confidentialDocumentsSharedToAulaForUserDriveMap[userId]
      ? state.confidentialDocumentsSharedToAulaForUserDriveMap[userId][driveId]
      : [];
  },

  isConfidentialDocumentShareToAulaForUserIdOnDriveId: state => (userId, driveId, itemId) => {
    const shares = state.confidentialDocumentsSharedToAulaForUserDriveMap[userId]
      ? state.confidentialDocumentsSharedToAulaForUserDriveMap[userId][driveId]
      : [];

    if (shares) {
      return shares.some(s => s.itemId === itemId);
    }
    return false;
  },

  journalizedConfidentialDocumentsForUserOnDrive: state => (userId, driveId) =>
    state.confidentialDocumentsJournalizedPerUserMap[userId]
      ? state.confidentialDocumentsJournalizedPerUserMap[userId][driveId]
      : [],

  journalizedConfidentialDocumentsForSectionOnDrive: state => (sectionId, driveId) =>
    state.confidentialDocumentsJournalizedPerSectionMap[sectionId]
      ? state.confidentialDocumentsJournalizedPerSectionMap[sectionId][driveId]
      : [],

  hiddenSharedFiles: state => state.hiddenSharedFilesState
};

/**
 * Create upload file session on graph
 * @param { string } destinationDriveId DriveId of destination folder
 * @param { string } destinationItemId ItemId of destination folder
 * @param { string } filename Name of new file
 * @param { boolean } replaceItemOnConflict If true file will be replaced on conflict - else renamed
 * @returns { Promise<object> } Upload session response
 */
async function createUploadSession(destinationDriveId, destinationItemId, filename, replaceItemOnConflict) {
  return apiGraph.post(
    `${DriveItemHelper.getDriveUri(destinationDriveId)}/items/${DriveItemHelper.getItemId(
      destinationItemId
    )}:/${filename}:/createUploadSession`,
    {
      item: {
        "@microsoft.graph.conflictBehavior": replaceItemOnConflict ? "replace" : "rename"
      }
    }
  );
}

/**
 * Split a file into chunks for further processing
 * @param { string[] } file Array of file bytes
 * @param { number } cSize Chunk size
 * @returns { object[] } Array of chunks the file is split into
 */
function splitFileToChuncks(file, cSize) {
  let startPointer = 0;
  let endPointer = file.byteLength;
  let chunks = [];
  while (startPointer < endPointer) {
    let newStartPointer = startPointer + cSize;
    chunks.push(file.slice(startPointer, newStartPointer));
    startPointer = newStartPointer;
  }
  return chunks;
}
